import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { Box } from '../../atoms/Box'
import { Text } from '../../atoms/Text'
import { Loading } from '../../atoms/Loading'
import { Button } from '../../atoms/Button'
import { ButtonGroup } from '../../molecules/ButtonGroup'
import { StatsContainer } from '../../molecules/StatsContainer'
import {
  ChartContainer,
  ChartSection,
  ChartGrid,
  ChartTitle,
  ChartLegend,
  ErrorContainer,
  NotAvailable,
  PrintWrapper,
} from '../../molecules/ChartComponents'
import {
  formatPrice,
  formatPricePerSqm,
  getColor,
  getColorSqm,
  calculatePriceThresholds,
  getPriceGeographyLegendItems,
  getPricePerSqmLegendItems,
  processLocationData,
  generateInsights,
} from './PriceGeographyChart.utils'
import {
  PriceGeographyChartProps,
  PriceGeographyChartRef,
  ProcessedLocationData,
  KPIData,
} from './PriceGeographyChart.types'

const PRICE_TYPES = [
  { type: 'median', label: 'Median' },
  { type: 'average', label: 'Average' },
] as const

export const PriceGeographyChart = forwardRef<
  PriceGeographyChartRef,
  PriceGeographyChartProps
>(
  (
    {
      data,
      loading = false,
      error = null,
      notAvailable = false,
      isRental = false,
      showInsights = true,
      showControls = true,
      accentColor = '#00ade6',
      t = (key: string) => key,
    },
    ref
  ) => {
    const treemapChartRef = useRef<HTMLDivElement>(null)
    const barChartRef = useRef<HTMLDivElement>(null)
    const sqmChartRef = useRef<HTMLDivElement>(null)
    const [priceType, setPriceType] = useState<'median' | 'average'>('median')

    const treemapInstance = useRef<echarts.ECharts | null>(null)
    const barChartInstance = useRef<echarts.ECharts | null>(null)
    const sqmChartInstance = useRef<echarts.ECharts | null>(null)
    const isPrintingRef = useRef(false)

    // Process data based on aggregation type
    let processedData: ProcessedLocationData[] = []

    if (data.by_canton) {
      processedData = processLocationData(data.by_canton.buckets, 'canton')
    } else if (data.by_district) {
      processedData = processLocationData(data.by_district.buckets, 'district')
    } else if (data.by_municipality) {
      processedData = processLocationData(
        data.by_municipality.buckets,
        'municipality'
      )
    }

    // Sort by selected price type
    processedData.sort((a, b) => {
      const aPrice = priceType === 'median' ? a.medianPrice : a.avgPrice
      const bPrice = priceType === 'median' ? b.medianPrice : b.avgPrice
      return bPrice - aPrice
    })

    // Get national values
    const nationalMedian = data.total_median?.values['50.0'] || 0
    const nationalMedianPerSqm = data.total_median_per_sqm?.values['50.0'] || 0
    const nationalAvg = data.total_avg?.value || 0
    const nationalAvgPerSqm = data.total_avg_per_sqm?.value || 0

    // Generate insights
    const insights = generateInsights(
      processedData,
      priceType,
      priceType === 'median' ? nationalMedian : nationalAvg,
      priceType === 'median' ? nationalMedianPerSqm : nationalAvgPerSqm,
      isRental,
      t
    )

    // Sort processed data by selected price type
    const sortedData = [...processedData].sort((a, b) => {
      const aPrice = priceType === 'median' ? a.medianPrice : a.avgPrice
      const bPrice = priceType === 'median' ? b.medianPrice : b.avgPrice
      return bPrice - aPrice
    })

    // Get highest and lowest for KPIs
    const highest = sortedData[0] || { name: '', medianPrice: 0, avgPrice: 0 }
    const lowest = sortedData[sortedData.length - 1] || {
      name: '',
      medianPrice: 0,
      avgPrice: 0,
    }

    // Prepare KPI data
    const kpiData: KPIData[] = [
      {
        id: 'national-median',
        value: formatPrice(
          priceType === 'median' ? nationalMedian : nationalAvg,
          isRental
        ),
        label: `National ${priceType === 'median' ? 'Median' : 'Average'} Price`,
        variant: 'default',
      },
      {
        id: 'national-median-sqm',
        value: formatPricePerSqm(
          priceType === 'median' ? nationalMedianPerSqm : nationalAvgPerSqm
        ),
        label: `National ${priceType === 'median' ? 'Median' : 'Average'} Price / m²`,
        variant: 'default',
      },
      {
        id: 'highest',
        value: formatPrice(
          priceType === 'median' ? highest.medianPrice : highest.avgPrice,
          isRental
        ),
        label: `Highest (${highest.name})`,
        variant: 'default',
      },
      {
        id: 'lowest',
        value: formatPrice(
          priceType === 'median' ? lowest.medianPrice : lowest.avgPrice,
          isRental
        ),
        label: `Lowest (${lowest.name})`,
        variant: 'default',
      },
    ]

    // Expose export methods via ref
    useImperativeHandle(ref, () => ({
      exportChart: () => {
        // Export the treemap chart as it's the main visualization
        if (treemapInstance.current) {
          // Force a render to ensure labels are displayed
          treemapInstance.current.resize()

          return treemapInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      exportBarChart: () => {
        if (barChartInstance.current) {
          barChartInstance.current.resize()
          return barChartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      exportSqmChart: () => {
        if (sqmChartInstance.current) {
          sqmChartInstance.current.resize()
          return sqmChartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#fff',
            excludeComponents: ['toolbox', 'dataZoom'],
          })
        }
        return null
      },
      getLegendData: () => sortedData,
      getSqmLegendData: () => {
        return [...sortedData]
          .filter(
            (d) =>
              (priceType === 'median'
                ? d.medianPricePerSqm
                : d.avgPricePerSqm) > 0
          )
          .sort((a, b) => {
            const aPrice =
              priceType === 'median' ? a.medianPricePerSqm : a.avgPricePerSqm
            const bPrice =
              priceType === 'median' ? b.medianPricePerSqm : b.avgPricePerSqm
            return bPrice - aPrice
          })
      },
      getKPIs: () => kpiData,
      getInsights: () => insights,
    }))

    useEffect(() => {
      if (!loading && !error && !notAvailable && sortedData.length > 0) {
        // Add a small delay to ensure DOM is ready
        const timer = setTimeout(() => {
          createCharts()
        }, 100)

        return () => clearTimeout(timer)
      }

      return () => {
        treemapInstance.current?.dispose()
        barChartInstance.current?.dispose()
        sqmChartInstance.current?.dispose()
      }
    }, [sortedData, loading, error, notAvailable, priceType, isRental])

    const createCharts = () => {
      if (
        !treemapChartRef.current ||
        !barChartRef.current ||
        !sqmChartRef.current
      )
        return

      // Create Treemap
      if (treemapInstance.current) {
        treemapInstance.current.dispose()
      }

      treemapInstance.current = echarts.init(
        treemapChartRef.current,
        undefined,
        {
          renderer: 'canvas',
          devicePixelRatio: isPrintingRef.current
            ? 1
            : window.devicePixelRatio || 1,
        }
      )

      // Force resize if in print mode
      if (isPrintingRef.current) {
        setTimeout(() => {
          treemapInstance.current?.resize()
        }, 50)
      }

      // Calculate price thresholds for dynamic coloring
      const prices = sortedData
        .map((d) => (priceType === 'median' ? d.medianPrice : d.avgPrice))
        .filter((p) => p > 0)
      const priceThresholds = calculatePriceThresholds(prices)

      const treemapData = sortedData.map((location) => {
        const price =
          priceType === 'median' ? location.medianPrice : location.avgPrice
        const pricePerSqm =
          priceType === 'median'
            ? location.medianPricePerSqm
            : location.avgPricePerSqm
        return {
          name: location.name,
          value: location.listingCount,
          medianPrice: price,
          medianPricePerSqm: pricePerSqm,
          itemStyle: {
            color: getColor(price, priceThresholds),
          },
          label: {
            show: true,
            formatter: (params: any) => {
              return `${params.name}\n${formatPrice(params.data.medianPrice, isRental)}`
            },
            fontSize: location.listingCount > 50 ? 14 : 11,
            color: '#fff',
            fontWeight: 600,
          },
        }
      })

      const treemapOption = {
        backgroundColor: 'transparent',
        tooltip: {
          formatter: (params: any) => {
            return `
            <div style="padding: 12px;">
              <div style="font-weight: 600; margin-bottom: 8px; font-size: 16px;">${params.name}</div>
              <div style="margin-bottom: 8px;">
                <div style="color: #6c757d; font-size: 12px;">${priceType === 'median' ? 'Median' : 'Average'} Price</div>
                <div style="color: #2563eb; font-size: 20px; font-weight: 700;">
                  ${formatPrice(params.data.medianPrice, isRental)}
                </div>
              </div>
              <div style="margin-bottom: 4px;">
                <div style="color: #6c757d; font-size: 12px;">${priceType === 'median' ? 'Median' : 'Average'} Price /m²</div>
                <div style="color: #1a1a1a; font-size: 14px; font-weight: 600;">
                  ${formatPricePerSqm(params.data.medianPricePerSqm || 0)}
                </div>
              </div>
              <div>
                <div style="color: #6c757d; font-size: 12px;">Number of Listings</div>
                <div style="color: #1a1a1a; font-size: 14px; font-weight: 600;">
                  ${params.value.toLocaleString()}
                </div>
              </div>
            </div>
          `
          },
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
          shadowBlur: 10,
          shadowColor: 'rgba(0, 0, 0, 0.1)',
          shadowOffsetX: 0,
          shadowOffsetY: 4,
        },
        series: [
          {
            type: 'treemap',
            data: treemapData,
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            roam: false,
            nodeClick: false,
            breadcrumb: {
              show: false,
            },
            levels: [
              {
                itemStyle: {
                  borderColor: '#fff',
                  borderWidth: 3,
                  gapWidth: 3,
                },
              },
            ],
            emphasis: {
              itemStyle: {
                borderColor: '#1e40af',
                borderWidth: 4,
                shadowBlur: 20,
                shadowColor: 'rgba(0, 0, 0, 0.3)',
              },
            },
          },
        ],
      }

      treemapInstance.current.setOption(treemapOption)

      // Create Bar Chart
      if (barChartInstance.current) {
        barChartInstance.current.dispose()
      }
      barChartInstance.current = echarts.init(barChartRef.current, undefined, {
        renderer: 'canvas',
        devicePixelRatio: isPrintingRef.current
          ? 1
          : window.devicePixelRatio || 1,
      })

      // Force resize if in print mode
      if (isPrintingRef.current) {
        setTimeout(() => {
          barChartInstance.current?.resize()
        }, 50)
      }

      const barOption = {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            const param = params[0]
            const location = sortedData.find((d) => d.name === param.name)
            return `
            <div style="padding: 12px;">
              <div style="font-weight: 600; margin-bottom: 8px;">${param.name}</div>
              <div style="color: #2563eb; font-size: 18px; font-weight: 600;">
                ${formatPrice(param.value, isRental)}
              </div>
              <div style="color: #6c757d; font-size: 12px; margin-top: 4px;">
                ${location?.listingCount.toLocaleString()} listings
              </div>
            </div>
          `
          },
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
        },
        grid: {
          left: '5%',
          right: '20%',
          bottom: '5%',
          top: '5%',
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value: number) => {
              // Use formatPrice for consistency with rental decimal places
              return formatPrice(value, isRental)
            },
            color: '#6c757d',
          },
          axisLine: {
            lineStyle: {
              color: '#e5e7eb',
            },
          },
          splitLine: {
            lineStyle: {
              color: '#f3f4f6',
              type: 'dashed',
            },
          },
          max:
            Math.max(
              ...sortedData.map((d) =>
                priceType === 'median' ? d.medianPrice : d.avgPrice
              )
            ) * 1.1,
        },
        yAxis: {
          type: 'category',
          data: sortedData.map((d) => d.name),
          inverse: true,
          axisLabel: {
            color: '#4b5563',
            fontSize: 11,
          },
          axisLine: {
            lineStyle: {
              color: '#e5e7eb',
            },
          },
          splitLine: {
            show: false,
          },
        },
        series: [
          {
            name: 'Median Price',
            type: 'bar',
            data: (() => {
              // Calculate price thresholds for dynamic coloring
              const prices = sortedData
                .map((d) =>
                  priceType === 'median' ? d.medianPrice : d.avgPrice
                )
                .filter((p) => p > 0)
              const priceThresholds = calculatePriceThresholds(prices)

              return sortedData.map((d) => {
                const price =
                  priceType === 'median' ? d.medianPrice : d.avgPrice
                return {
                  value: price,
                  itemStyle: {
                    color: getColor(price, priceThresholds),
                    borderRadius: [0, 4, 4, 0],
                  },
                }
              })
            })(),
            barWidth: '70%',
            label: {
              show: true,
              position: 'right',
              formatter: (params: any) => {
                return formatPrice(params.value, isRental)
              },
              color: '#4b5563',
              fontSize: 10,
            },
          },
        ],
      }

      barChartInstance.current.setOption(barOption)

      // Create Price per Square Meter Chart
      if (sqmChartInstance.current) {
        sqmChartInstance.current.dispose()
      }
      sqmChartInstance.current = echarts.init(sqmChartRef.current, undefined, {
        renderer: 'canvas',
        devicePixelRatio: isPrintingRef.current
          ? 1
          : window.devicePixelRatio || 1,
      })

      // Force resize if in print mode
      if (isPrintingRef.current) {
        setTimeout(() => {
          sqmChartInstance.current?.resize()
        }, 50)
      }

      const sqmData = sortedData
        .filter(
          (d) =>
            (priceType === 'median' ? d.medianPricePerSqm : d.avgPricePerSqm) >
            0
        )
        .sort((a, b) => {
          const aPrice =
            priceType === 'median' ? a.medianPricePerSqm : a.avgPricePerSqm
          const bPrice =
            priceType === 'median' ? b.medianPricePerSqm : b.avgPricePerSqm
          return bPrice - aPrice
        })

      // Calculate price per sqm thresholds for dynamic coloring
      const pricesPerSqm = sqmData.map((d) =>
        priceType === 'median' ? d.medianPricePerSqm : d.avgPricePerSqm
      )
      const pricePerSqmThresholds = calculatePriceThresholds(pricesPerSqm)

      const sqmOption = {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            const param = params[0]
            const location = sqmData.find((d) => d.name === param.name)
            return `
            <div style="padding: 12px;">
              <div style="font-weight: 600; margin-bottom: 8px;">${param.name}</div>
              <div style="color: #8b5cf6; font-size: 18px; font-weight: 600;">
                ${formatPricePerSqm(param.value)}
              </div>
              <div style="color: #6c757d; font-size: 12px; margin-top: 4px;">
                Total ${priceType}: ${formatPrice(location ? (priceType === 'median' ? location.medianPrice : location.avgPrice) : 0, isRental)}
              </div>
            </div>
          `
          },
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          borderRadius: 8,
        },
        grid: {
          left: '5%',
          right: '20%',
          bottom: '5%',
          top: '5%',
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value: number) => {
              return value.toLocaleString() + ' CHF/m²'
            },
            color: '#6c757d',
            fontSize: 11,
          },
          axisLine: {
            lineStyle: {
              color: '#e5e7eb',
            },
          },
          splitLine: {
            lineStyle: {
              color: '#f3f4f6',
              type: 'dashed',
            },
          },
        },
        yAxis: {
          type: 'category',
          data: sqmData.map((d) => d.name),
          inverse: true,
          axisLabel: {
            color: '#4b5563',
            fontSize: 11,
          },
          axisLine: {
            lineStyle: {
              color: '#e5e7eb',
            },
          },
          splitLine: {
            show: false,
          },
        },
        series: [
          {
            name: 'Price per m²',
            type: 'bar',
            data: sqmData.map((d) => {
              const pricePerSqm =
                priceType === 'median' ? d.medianPricePerSqm : d.avgPricePerSqm
              return {
                value: pricePerSqm,
                itemStyle: {
                  color: getColorSqm(pricePerSqm, pricePerSqmThresholds),
                  borderRadius: [0, 4, 4, 0],
                },
              }
            }),
            barWidth: '65%',
            label: {
              show: true,
              position: 'right',
              formatter: (params: any) => {
                return formatPricePerSqm(params.value)
              },
              color: '#4b5563',
              fontSize: 10,
            },
          },
        ],
      }

      sqmChartInstance.current.setOption(sqmOption)
    }

    // Handle window resize and print events
    useEffect(() => {
      const handleResize = () => {
        treemapInstance.current?.resize()
        barChartInstance.current?.resize()
        sqmChartInstance.current?.resize()
      }

      // Handle print events
      const handleBeforePrint = () => {
        isPrintingRef.current = true

        // Force charts to resize for print layout
        // Use multiple timeouts to ensure charts are resized properly
        const resizeCharts = () => {
          if (treemapInstance.current) {
            treemapInstance.current.resize()
          }
          if (barChartInstance.current) {
            barChartInstance.current.resize()
          }
          if (sqmChartInstance.current) {
            sqmChartInstance.current.resize()
          }
        }

        // Resize immediately and after delays to catch different print dialog timings
        resizeCharts()
        setTimeout(resizeCharts, 50)
        setTimeout(resizeCharts, 150)
        setTimeout(resizeCharts, 300)
      }

      const handleAfterPrint = () => {
        isPrintingRef.current = false

        // Resize charts back to screen layout
        setTimeout(() => {
          treemapInstance.current?.resize()
          barChartInstance.current?.resize()
          sqmChartInstance.current?.resize()
        }, 100)
      }

      // Media query listener for print
      const mediaQueryList = window.matchMedia('print')
      const handleMediaQueryChange = (e: any) => {
        if (e.matches) {
          handleBeforePrint()
        } else {
          handleAfterPrint()
        }
      }

      // Check initial state
      if (mediaQueryList.matches) {
        handleBeforePrint()
      }

      window.addEventListener('resize', handleResize)
      window.addEventListener('beforeprint', handleBeforePrint)
      window.addEventListener('afterprint', handleAfterPrint)

      // Modern browsers
      if (mediaQueryList.addEventListener) {
        mediaQueryList.addEventListener('change', handleMediaQueryChange)
      } else {
        // Legacy browsers
        mediaQueryList.addListener(handleMediaQueryChange)
      }

      return () => {
        window.removeEventListener('resize', handleResize)
        window.removeEventListener('beforeprint', handleBeforePrint)
        window.removeEventListener('afterprint', handleAfterPrint)

        if (mediaQueryList.removeEventListener) {
          mediaQueryList.removeEventListener('change', handleMediaQueryChange)
        } else {
          mediaQueryList.removeListener(handleMediaQueryChange)
        }
      }
    }, [])

    const handlePriceTypeChange = (type: 'median' | 'average') => {
      if (setPriceType) {
        setPriceType(type)
      }
    }

    if (loading) {
      return (
        <Box style={{ textAlign: 'center', padding: '40px' }}>
          <Loading variant="spinner" size="large" center />
          <Text variant="body1" color="secondary" style={{ marginTop: '16px' }}>
            Loading price geography data...
          </Text>
        </Box>
      )
    }

    if (error) {
      return (
        <ErrorContainer variant="error">
          <Text variant="body1">{error}</Text>
        </ErrorContainer>
      )
    }

    if (notAvailable) {
      return (
        <NotAvailable
          title="Not Available at This Location Level"
          description="Price geography analysis is not available at the municipality or postal code level. Please select a region or district to view geographic price distribution."
        />
      )
    }

    if (data.length === 0) {
      return (
        <ErrorContainer variant="info">
          <Text variant="body1">
            No data available for the selected filters.
          </Text>
        </ErrorContainer>
      )
    }

    return (
      <PrintWrapper>
        {showControls && (
          <Box marginBottom="24px">
            <Box display="flex" justifyContent="flex-end">
              <ButtonGroup>
                {PRICE_TYPES.map((pt) => (
                  <Button
                    key={pt.type}
                    variant={priceType === pt.type ? 'primary' : 'secondary'}
                    size="small"
                    onClick={() => handlePriceTypeChange(pt.type)}
                  >
                    {pt.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
          </Box>
        )}

        <Box marginBottom="30px">
          <StatsContainer
            fullWidth
            stats={[
              {
                id: 'national-median',
                value: formatPrice(
                  priceType === 'median' ? nationalMedian : nationalAvg,
                  isRental
                ),
                label: `National ${priceType === 'median' ? 'Median' : 'Average'} Price`,
                variant: 'default',
              },
              {
                id: 'national-median-sqm',
                value: formatPricePerSqm(
                  priceType === 'median'
                    ? nationalMedianPerSqm
                    : nationalAvgPerSqm
                ),
                label: `National ${priceType === 'median' ? 'Median' : 'Average'} Price / m²`,
                variant: 'default',
              },
              {
                id: 'highest',
                value: formatPrice(
                  priceType === 'median'
                    ? highest.medianPrice
                    : highest.avgPrice,
                  isRental
                ),
                label: `Highest (${highest.name})`,
                variant: 'default',
              },
              {
                id: 'lowest',
                value: formatPrice(
                  priceType === 'median' ? lowest.medianPrice : lowest.avgPrice,
                  isRental
                ),
                label: `Lowest (${lowest.name})`,
                variant: 'default',
              },
            ]}
          />
        </Box>

        <ChartGrid>
          <ChartSection
            title={`Geographic Volume Distribution (${priceType === 'median' ? 'Median' : 'Average'} Prices)`}
          >
            <ChartContainer height={600} ref={treemapChartRef} />
          </ChartSection>
          <ChartSection
            title={`Rankings by ${priceType === 'median' ? 'Median' : 'Average'} Price`}
          >
            <ChartContainer height={600} ref={barChartRef} />
          </ChartSection>
        </ChartGrid>

        <Box marginBottom="30px">
          <ChartLegend
            items={getPriceGeographyLegendItems(
              sortedData,
              priceType,
              isRental
            )}
          />
        </Box>

        <Box marginBottom="30px">
          <ChartTitle>Rankings by Price per Square Meter</ChartTitle>
          <ChartContainer
            height={500}
            ref={sqmChartRef}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
            }}
          />
        </Box>

        <ChartLegend items={getPricePerSqmLegendItems(sortedData, priceType)} />

        {showInsights && insights.length > 0 && (
          <Box marginTop="30px">
            <ChartTitle style={{ marginBottom: '24px' }}>
              Key Insights
            </ChartTitle>
            <Box display="flex" flexWrap="wrap" gap="24px">
              {insights.map((insight, index) => (
                <Box
                  key={index}
                  flex="1 1 280px"
                  style={{
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    padding: '20px',
                    borderLeft: `4px solid ${accentColor}`,
                    minWidth: 0,
                  }}
                >
                  <Text
                    variant="h4"
                    style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      marginBottom: '8px',
                      color: '#1a1a1a',
                    }}
                  >
                    {insight.title}
                  </Text>
                  <Text
                    variant="body2"
                    style={{
                      fontSize: '14px',
                      lineHeight: 1.5,
                      color: '#6c757d',
                    }}
                  >
                    {insight.description}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </PrintWrapper>
    )
  }
)

PriceGeographyChart.displayName = 'PriceGeographyChart'
